import type { Poem, PoemTheme } from '@maria-cavalheiro-website/shared-types'
import { findCollection } from '@maria-cavalheiro-website/shared-types/config'
import type { ChipTone, IconName } from '@maria-cavalheiro-website/shared-ui-web'

import { poemPath } from '../../app/routes.ts'
import type { GroupingMode } from './poem-groups.ts'

export interface PoemCardView {
  readonly slug: string
  readonly title: string
  readonly href: string
  readonly icon: IconName
  readonly tone: ChipTone
  /** Small qualifier in the card's top-right: a theme, or a collection badge. */
  readonly badge: string
}

/**
 * Theme -> chip tone. Mirrors the design's `PALETA` map.
 *
 * A frozen record rather than a chain of conditionals: the mapping is total over
 * the theme union, so TypeScript proves exhaustiveness and there is no unreachable
 * default arm to leave uncovered.
 */
const TONE_BY_THEME: Record<PoemTheme, ChipTone> = Object.freeze({
  Pássaros: 'bird',
  Natureza: 'leaf',
  Casa: 'warm',
  Memória: 'mem',
})

/**
 * Pure `Poem -> PoemCardView`.
 *
 * The badge is mode-dependent: in `colecao` mode the group heading already says
 * which collection this is, so repeating it on every card would be noise — the
 * theme is the useful thing there. In every other mode the collection is the
 * information the grouping has hidden.
 *
 * `mode` is a parameter rather than a module-level read, so this stays pure and
 * testable without a fixture for the surrounding component.
 */
export function poemCardView(poem: Poem, mode: GroupingMode): PoemCardView {
  const collection = findCollection(poem.collection)

  return {
    slug: poem.slug,
    title: poem.title,
    href: poemPath(poem.slug),
    icon: poem.icon,
    tone: TONE_BY_THEME[poem.theme],
    badge: mode === 'colecao' ? poem.theme : (collection?.badge ?? ''),
  }
}
